<template>
  <div
    class="post-preview"
    :class="{
      'no-image': !showImage,
      'no-author': !showAuthor,
      'no-read-time': !showReadTime,
      [`shadow-${shadow}`]: shadow,
    }"
  >
    <NuxtLink :href="`/${post.fullSlug}`">
      <div class="image-container" v-if="showImage">
        <StoryblokImg
          :image="post.featuredImage"
          :class="`image-size-${imageSize}`"
          width="630"
          placeholder
        />
      </div>

      <div class="label-container">
        <CjaLabel :color="'blue'">{{ post.category }}</CjaLabel>
      </div>
      <div class="text-container">
        <div class="text-wrapper">
          <div class="title">
            {{ post.title }}
          </div>
          <div class="excerpt" v-if="showExcerpt">
            <RichText :text="post.excerpt" />
          </div>
        </div>
      </div>
      <div
        class="author-container"
        :class="{ 'bi-author': post.specialist }"
        v-if="showAuthor"
      >
        <div class="author-image-wrapper">
          <StoryblokImg :image="post.author?.image" width="36" height="36" />
          <StoryblokImg
            v-if="post.specialist"
            :image="post.specialist?.image"
            width="36"
            height="36"
          />
        </div>
        <div class="author-name">
          {{
            t(`author_name.${post.specialist ? "2" : "1"}`, {
              author: post.author?.name,
              specialist: post.specialist?.name,
            })
          }}
        </div>
      </div>
      <div class="read-time-container" v-if="showReadTime">
        <span class="m-cgg-icon--time"></span>
        {{ post.estimatedReadTime }}
      </div>
    </NuxtLink>
  </div>
</template>

<script lang="ts" setup>
import { getEstimatedReadTime } from "#imports";
import { Post } from "@/types/Post";

const props = withDefaults(
  defineProps<{
    post: Post;
    showImage?: boolean;
    showAuthor?: boolean;
    showReadTime?: boolean;
    showExcerpt?: boolean;
    imageSize?: "md" | "lg";
    shadow?: "sm" | "md";
  }>(),
  {
    showImage: true,
    showAuthor: true,
    showReadTime: true,
    showExcerpt: false,
    imageSize: "md",
    shadow: "sm",
  }
);

const { t } = useI18n();
</script>

<style lang="scss" scoped>
.post-preview {
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid $light-grey;
  background: $white;
  user-select: none;
  transition: all 0.6s ease-in-out;

  &.shadow-sm:hover {
    @media screen and (min-width: $break-lg-min) {
      box-shadow: $box-shadow-s;
    }
  }

  &.shadow-md:hover {
    @media screen and (min-width: $break-lg-min) {
      box-shadow: $box-shadow-m-dark;
    }
  }

  a {
    position: relative;
    cursor: pointer;
    color: inherit;
    height: 100%;
    padding: 16px;
    display: grid;
    grid-template-columns: 1fr auto;
    grid-template-rows: auto 1fr auto;
    gap: 16px;

    &:hover {
      text-decoration: none;
    }
  }

  .image-container {
    grid-area: 1 / 1 / 2 / 3;
    margin: -16px -16px 0;
    overflow: hidden;
    background-color: $dark-white;

    img {
      display: block;
      width: 100%;
      object-fit: cover;
      filter: brightness(0.9);

      &.image-size-md {
        height: 70px;

        @media screen and (min-width: $break-md-min) {
          height: 120px;
        }
      }

      &.image-size-lg {
        height: 120px;

        @media screen and (min-width: $break-md-min) {
          height: 120px;
        }

        @media screen and (min-width: $break-lg-min) {
          height: 200px;
        }
      }
    }
  }

  .label-container {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 8px;
    position: absolute;
    top: 16px;
    left: 16px;
    right: 16px;
  }

  .text-container {
    grid-area: 2 / 1 / 3 / 3;
    .text-wrapper {
      grid-area: 1 / 1 / 2 / 3;

      .title {
        font-size: 16px;
        line-height: 20px;
        font-weight: 700;

        @media screen and (min-width: $break-md-min) {
          font-size: 18px;
          line-height: 22px;
        }
      }

      .excerpt {
        font-size: 16px;
        line-height: 22px;
        -webkit-line-clamp: 2;
        overflow: hidden;
        display: -webkit-box;
        -webkit-box-orient: vertical;

        @media screen and (max-width: $break-sm-max) {
          display: none;
        }
      }
    }
  }

  .author-container {
    grid-area: 3 / 1 / 4 / 2;
    display: grid;
    grid-template-columns: 36px 1fr;
    align-items: center;
    gap: 0 8px;
    font-size: 14px;
    line-height: 16px;

    &.bi-author {
      grid-template-columns: 58px 1fr;

      .author-image-wrapper {
        display: flex;

        img + img {
          position: relative;
          left: -14px;
        }
      }
    }

    .author-image-wrapper {
      grid-area: 1 / 1 / 2 / 2;

      img {
        flex-shrink: 0;
        max-width: 36px;
        width: 36px;
        height: 36px;
        overflow: hidden;
        display: block;
        width: 100%;
        border-radius: 50%;
        border: 2px solid $white;
      }
    }

    .author-name {
      grid-area: 1 / 2 / 2 / 3;
    }
  }

  .read-time-container {
    grid-area: 3 / 2 / 4 / 3;
    display: flex;
    align-items: center;
    gap: 8px;
    color: $main-blue;
    font-size: 14px;
    line-height: 24px;

    span {
      font-size: 24px;
    }
  }

  &.no-image {
    a {
      grid-template-rows: auto 1fr auto;
    }

    .label-container {
      position: static;
      grid-area: 1 / 1 / 2 / 3;
    }
  }

  &.no-image.no-author {
    a {
      grid-template-rows: auto 1fr;
    }

    .label-container {
      position: static;
      grid-area: 1 / 1 / 2 / 2;
    }

    .read-time-container {
      grid-area: 1 / 2 / 2 / 3;
    }
  }

  &.no-author.no-read-time {
    a {
      grid-template-rows: auto 1fr;
    }
  }
}
</style>

<i18n lang="json">
{
  "pt": {
    "author_name": {
      "1": "Por {author}",
      "2": "Por {author} e {specialist}"
    }
  }
}
</i18n>
